/**
 * Entity Workbench — one place where an entity is fully visible.
 * ENTITY_WORKBENCH_PLAN.md W1 (route + header + Overview) + W2 (Identity
 * & data quality, delivering substrate-plan E5) + W8 (Contacts) + W7
 * (Manage — the edit surfaces the /entities expander used to carry).
 *
 * ACCOUNTABLE surface: every value carries its source, confidence,
 * evidence and as-at stamp. Conflicts surface for a human decision —
 * nothing auto-resolves. Evidence, not badges.
 */
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  ArrowLeft, Building2, CalendarClock, CheckCircle2, FileText,
  Fingerprint, Image as ImageIcon, LayoutDashboard, Network, Newspaper,
  ShieldQuestion, Settings, Users, XCircle,
} from 'lucide-react';
import {
  getEntityIdentity, getEntityOverview, triageIdentityCandidate,
  type PresenceRow, type TriageAction, type WorkbenchHeader, type WorkbenchOverview,
} from '../lib/api/entity-workbench.js';
import { ContactsTab } from '../components/entity/ContactsTab.js';
import { ImagesTab } from '../components/entity/ImagesTab.js';
import { ManageTab } from '../components/entity/ManageTab.js';
import { RelationshipsTab } from '../components/entity/RelationshipsTab.js';

type Tab = 'overview' | 'identity' | 'contacts' | 'relationships' | 'images' | 'manage';

const TABS: { key: Tab; label: string; icon: typeof LayoutDashboard }[] = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard },
  { key: 'identity', label: 'Identity & data quality', icon: Fingerprint },
  { key: 'contacts', label: 'Contacts', icon: Users },
  { key: 'relationships', label: 'Relationships', icon: Network },
  { key: 'images', label: 'Images', icon: ImageIcon },
  { key: 'manage', label: 'Manage', icon: Settings },
];

export default function EntityWorkbenchPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as Tab) || 'overview';
  const setActiveTab = (tab: Tab) => setSearchParams({ tab });

  const overviewQuery = useQuery({
    queryKey: ['entity-workbench-overview', id],
    queryFn: () => getEntityOverview(id!),
    enabled: !!id,
  });

  const header = overviewQuery.data?.header;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
      <Link
        to="/entities"
        className="inline-flex items-center gap-1.5 text-sm text-grey-500 hover:text-grey-700"
      >
        <ArrowLeft className="h-4 w-4" /> Entities
      </Link>

      {overviewQuery.isLoading && (
        <div className="py-16 text-center text-sm text-grey-500">Loading entity…</div>
      )}
      {overviewQuery.isError && (
        <div className="py-16 text-center text-sm text-red-600">
          Could not load this entity.
        </div>
      )}

      {header && (
        <>
          <WorkbenchHeaderBlock header={header} onOpenIdentity={() => setActiveTab('identity')} />

          <div className="flex border-b border-grey-200 overflow-x-auto">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              const badge =
                tab.key === 'identity' && header.candidates_awaiting_triage
                  ? header.candidates_awaiting_triage
                  : null;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                    isActive
                      ? 'border-primary-600 text-primary-700'
                      : 'border-transparent text-grey-500 hover:text-grey-700 hover:border-grey-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                  {badge != null && (
                    <span className="ml-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                      {badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {activeTab === 'overview' && <OverviewTab data={overviewQuery.data!} />}
          {activeTab === 'identity' && <IdentityTab entityId={id!} />}
          {activeTab === 'contacts' && <ContactsTab entityId={id!} />}
          {activeTab === 'relationships' && <RelationshipsTab entityId={id!} />}
          {activeTab === 'images' && <ImagesTab entityId={id!} />}
          {activeTab === 'manage' && <ManageTab entityId={id!} />}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function WorkbenchHeaderBlock({ header, onOpenIdentity }: {
  header: WorkbenchHeader;
  onOpenIdentity: () => void;
}) {
  return (
    <div className="bg-white rounded-2xl border border-grey-100 p-5 space-y-3">
      <div className="flex flex-wrap items-center gap-3 min-w-0">
        <Building2 className="h-6 w-6 text-grey-400 shrink-0" />
        <span className="font-mono font-bold text-lg text-grey-900">
          {header.exchange}:{header.ticker}
        </span>
        <h1 className="text-xl font-bold text-grey-900 truncate">{header.display_name}</h1>
        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700">
          {header.entity_type}
        </span>
        {header.index_badges.map(b => (
          <span
            key={`${b.exchange}:${b.index_code}`}
            title={`state: ${b.state} · source: index_membership`}
            className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
              b.state === 'current'
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-amber-50 text-amber-700'
            }`}
          >
            {b.index_code}{b.state !== 'current' ? ` (${b.state.replace('_', ' ')})` : ''}
          </span>
        ))}
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1.5 text-sm text-grey-600">
        {header.price?.close != null && (
          <span title={`source: ${header.price.source ?? 'daily_prices'}`}>
            <span className="font-semibold text-grey-900">${header.price.close.toFixed(3)}</span>
            {header.price.as_at && (
              <span className="text-grey-400"> as at {format(new Date(header.price.as_at), 'dd MMM')}</span>
            )}
          </span>
        )}
        <span>{header.articles_published} published articles</span>
        <span>
          approval: <span className="font-medium">{header.approval_status ?? '—'}</span>
        </span>
        <span>
          discovery: <span className="font-medium">{header.discovery_status ?? '—'}</span>
        </span>
        <span title="identity_refreshed_at — last projection from Cynapse">
          identity refreshed:{' '}
          <span className="font-medium">
            {header.identity_refreshed_at
              ? format(new Date(header.identity_refreshed_at), 'dd MMM yyyy')
              : 'never'}
          </span>
        </span>
        {header.candidates_awaiting_triage != null && header.candidates_awaiting_triage > 0 && (
          <button
            onClick={onOpenIdentity}
            className="inline-flex items-center gap-1 text-amber-700 font-medium hover:underline"
          >
            <ShieldQuestion className="h-4 w-4" />
            {header.candidates_awaiting_triage} identity candidate
            {header.candidates_awaiting_triage === 1 ? '' : 's'} awaiting review
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview tab
// ---------------------------------------------------------------------------

function OverviewTab({ data }: { data: WorkbenchOverview }) {
  const o = data.overview;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <section className="bg-white rounded-2xl border border-grey-100 p-5 lg:col-span-2">
        <h2 className="text-sm font-semibold text-grey-900 mb-2">Description</h2>
        {o.description ? (
          <>
            <p className="text-sm text-grey-700 whitespace-pre-line">{o.description}</p>
            {o.description_updated_at && (
              <p className="mt-2 text-xs text-grey-400">
                updated {format(new Date(o.description_updated_at), 'dd MMM yyyy')}
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-grey-400">No description held.</p>
        )}
        {o.sector && (
          <p className="mt-2 text-xs text-grey-500">sector: {o.sector}</p>
        )}
      </section>

      <section className="bg-white rounded-2xl border border-grey-100 p-5">
        <h2 className="text-sm font-semibold text-grey-900 mb-3 flex items-center gap-2">
          <FileText className="h-4 w-4 text-grey-400" /> Latest announcements
        </h2>
        {o.latest_announcements.length === 0 && (
          <p className="text-sm text-grey-400">None held.</p>
        )}
        <ul className="space-y-2">
          {o.latest_announcements.map(a => (
            <li key={a.announcement_id} className="text-sm min-w-0">
              <Link
                to={`/announcements/${a.announcement_id}`}
                className="text-grey-800 hover:text-primary-700 hover:underline line-clamp-1"
              >
                {a.title ?? '(untitled)'}
              </Link>
              <span className="text-xs text-grey-400">
                {a.date ? format(new Date(a.date), 'dd MMM yyyy') : '—'}
                {a.price_sensitive ? ' · price-sensitive' : ''}
                {a.source ? ` · ${a.source}` : ''}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-white rounded-2xl border border-grey-100 p-5">
        <h2 className="text-sm font-semibold text-grey-900 mb-3 flex items-center gap-2">
          <Newspaper className="h-4 w-4 text-grey-400" /> Latest articles
        </h2>
        {o.latest_articles.length === 0 && (
          <p className="text-sm text-grey-400">None held.</p>
        )}
        <ul className="space-y-2">
          {o.latest_articles.map(a => (
            <li key={a.article_id} className="text-sm min-w-0">
              <Link
                to={`/articles/${a.article_id}`}
                className="text-grey-800 hover:text-primary-700 hover:underline line-clamp-1"
              >
                {a.title ?? '(untitled)'}
              </Link>
              <span className="text-xs text-grey-400">
                {a.publish_status}
                {a.published_at ? ` · ${format(new Date(a.published_at), 'dd MMM yyyy')}` : ''}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-white rounded-2xl border border-grey-100 p-5">
        <h2 className="text-sm font-semibold text-grey-900 mb-3">Open signals</h2>
        {o.open_signals.length === 0 && <p className="text-sm text-grey-400">None open.</p>}
        <ul className="space-y-1.5">
          {o.open_signals.map(s => (
            <li key={s.signal_id} className="text-sm text-grey-700">
              <span className="font-medium">{s.signal_type}</span>
              <span className="text-xs text-grey-400">
                {' '}· {format(new Date(s.created_at), 'dd MMM yyyy')}
                {s.exchange ? ` · ${s.exchange}` : ''}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-white rounded-2xl border border-grey-100 p-5">
        <h2 className="text-sm font-semibold text-grey-900 mb-3 flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-grey-400" /> Next calendar event
        </h2>
        {o.next_calendar_event ? (
          <p className="text-sm text-grey-700">
            <span className="font-medium">{o.next_calendar_event.event_type}</span>
            {' '}on {format(new Date(o.next_calendar_event.event_date), 'dd MMM yyyy')}
            <span className="text-xs text-grey-400">
              {' '}· source: {o.next_calendar_event.source ?? '—'}
              {o.next_calendar_event.confidence
                ? ` · ${o.next_calendar_event.confidence}`
                : ''}
            </span>
          </p>
        ) : (
          <p className="text-sm text-grey-400">Nothing scheduled.</p>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Identity tab (W2 — delivers substrate-plan E5)
// ---------------------------------------------------------------------------

const KIND_LABELS: Record<string, string> = {
  domain: 'Website domain',
  x_handle: 'X handle',
  linkedin_url: 'LinkedIn',
  linkedin_urn: 'LinkedIn URN',
  ir_page: 'IR page',
};

function IdentityTab({ entityId }: { entityId: string }) {
  const queryClient = useQueryClient();
  const identityQuery = useQuery({
    queryKey: ['entity-workbench-identity', entityId],
    queryFn: () => getEntityIdentity(entityId),
  });

  const triageMutation = useMutation({
    mutationFn: (body: { presence_id: string; action: TriageAction }) =>
      triageIdentityCandidate(entityId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entity-workbench-identity', entityId] });
      queryClient.invalidateQueries({ queryKey: ['entity-workbench-overview', entityId] });
    },
  });

  if (identityQuery.isLoading) {
    return <div className="py-16 text-center text-sm text-grey-500">Loading identity…</div>;
  }
  if (identityQuery.isError || !identityQuery.data) {
    return <div className="py-16 text-center text-sm text-red-600">Could not load identity.</div>;
  }

  const { record, local, cynapse_entity_id } = identityQuery.data;
  const current = record.current ?? [];
  const candidates = record.candidates ?? [];
  const checks = record.latest_checks ?? [];

  return (
    <div className="space-y-4">
      {!cynapse_entity_id && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          This entity has no Cynapse binding — the identity record is unavailable until it
          is bound (Tools → Cynapse binding).
        </div>
      )}

      {/* The record — Cynapse, source of truth */}
      <section className="bg-white rounded-2xl border border-grey-100 p-5">
        <h2 className="text-sm font-semibold text-grey-900 mb-1">
          Identity record <span className="font-normal text-grey-400">— Cynapse, the source of record</span>
        </h2>
        {record.last_verification && (
          <p className="text-xs text-grey-400 mb-3">
            last verified {format(new Date(record.last_verification), 'dd MMM yyyy HH:mm')}
          </p>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-grey-100 text-left text-xs text-grey-500">
                <th className="py-2 pr-4">Attribute</th>
                <th className="py-2 pr-4">Value</th>
                <th className="py-2 pr-4">Source</th>
                <th className="py-2 pr-4">Confidence</th>
                <th className="py-2 pr-4">Sightings</th>
                <th className="py-2 pr-4">Observed</th>
                <th className="py-2">Reviewed by</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(KIND_LABELS).map(kind => {
                const row = current.find(r => r.attribute_kind === kind);
                const check = checks.find(c => c.attribute_kind === kind);
                return (
                  <tr key={kind} className="border-b border-grey-50">
                    <td className="py-2 pr-4 text-grey-600">{KIND_LABELS[kind]}</td>
                    <td className="py-2 pr-4 font-mono text-grey-900">
                      {row?.value_norm ??
                        (kind === 'domain' && record.canonical_domain
                          ? <span title="projection only — not yet validated into a substrate row">{record.canonical_domain} *</span>
                          : <span className="text-grey-300">—</span>)}
                    </td>
                    <td className="py-2 pr-4 text-xs text-grey-500">{row?.source ?? check?.source ?? '—'}</td>
                    <td className="py-2 pr-4 text-xs text-grey-500">
                      {row ? Number(row.confidence).toFixed(2) : '—'}
                    </td>
                    <td className="py-2 pr-4 text-xs text-grey-500">{row?.observation_count ?? '—'}</td>
                    <td className="py-2 pr-4 text-xs text-grey-500">
                      {row ? format(new Date(row.observed_at), 'dd MMM yy') :
                        check ? `${check.result} ${format(new Date(check.checked_at), 'dd MMM yy')}` : 'never checked'}
                    </td>
                    <td className="py-2 text-xs text-grey-500">{row?.reviewed_by ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {record.canonical_domain && !current.some(r => r.attribute_kind === 'domain') && (
          <p className="mt-2 text-xs text-grey-400">
            * legacy projection — becomes a provenanced substrate row when the validation
            sweep confirms it.
          </p>
        )}
      </section>

      {/* Candidates awaiting triage */}
      <section className="bg-white rounded-2xl border border-grey-100 p-5">
        <h2 className="text-sm font-semibold text-grey-900 mb-3">
          Candidates awaiting review
          {candidates.length > 0 && (
            <span className="ml-2 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
              {candidates.length}
            </span>
          )}
        </h2>
        {candidates.length === 0 && (
          <p className="text-sm text-grey-400">Nothing awaiting review.</p>
        )}
        <div className="space-y-2">
          {candidates.map(c => (
            <CandidateRow
              key={c.presence_id}
              candidate={c}
              currentValue={current.find(r => r.attribute_kind === c.attribute_kind)?.value_norm ?? null}
              busy={triageMutation.isPending}
              onAction={(action) => triageMutation.mutate({ presence_id: c.presence_id, action })}
            />
          ))}
        </div>
        {triageMutation.isError && (
          <p className="mt-2 text-xs text-red-600">Triage action failed — try again.</p>
        )}
      </section>

      {/* Local projection + codes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="bg-white rounded-2xl border border-grey-100 p-5">
          <h2 className="text-sm font-semibold text-grey-900 mb-3">
            Local projection <span className="font-normal text-grey-400">— Assayer, read-only mirror</span>
          </h2>
          <dl className="text-sm space-y-1.5">
            <Item label="canonical_domain" value={local.canonical_domain} />
            <Item label="linkedin_url" value={local.linkedin_url} />
            <Item label="linkedin_urn" value={local.linkedin_urn} />
            <Item label="twitter_handle" value={local.twitter_handle} />
            {Object.entries(local.legacy_domain_keys).map(([k, v]) => (
              <Item key={k} label={`${k} (legacy)`} value={v} muted />
            ))}
          </dl>
        </section>

        <section className="bg-white rounded-2xl border border-grey-100 p-5">
          <h2 className="text-sm font-semibold text-grey-900 mb-3">External codes & aliases</h2>
          <dl className="text-sm space-y-1.5">
            {(record.current_codes ?? []).map(c => (
              <Item key={`${c.code_system}:${c.code_value}`} label={c.code_system} value={c.code_value} />
            ))}
            {(record.securities ?? []).filter(s => !s.valid_to).map(s => (
              <Item
                key={`${s.exchange}:${s.code}`}
                label={`${s.exchange} ${s.security_class}${s.is_primary_line ? ' (primary)' : ''}`}
                value={s.code}
              />
            ))}
            {(record.active_aliases ?? []).map(a => (
              <Item key={a.alias_text} label={a.alias_kind ?? 'alias'} value={a.alias_text} muted />
            ))}
            {!(record.current_codes?.length || record.securities?.length || record.active_aliases?.length) && (
              <p className="text-sm text-grey-400">None held.</p>
            )}
          </dl>
        </section>
      </div>
    </div>
  );
}

function Item({ label, value, muted }: { label: string; value: string | null | undefined; muted?: boolean }) {
  return (
    <div className="flex gap-2 min-w-0">
      <dt className={`shrink-0 w-44 text-xs pt-0.5 ${muted ? 'text-grey-300' : 'text-grey-400'}`}>{label}</dt>
      <dd className={`font-mono text-sm truncate ${muted ? 'text-grey-400' : 'text-grey-800'}`}>
        {value ?? <span className="text-grey-300">—</span>}
      </dd>
    </div>
  );
}

function CandidateRow({ candidate, currentValue, busy, onAction }: {
  candidate: PresenceRow;
  currentValue: string | null;
  busy: boolean;
  onAction: (action: TriageAction) => void;
}) {
  const [confirming, setConfirming] = useState<TriageAction | null>(null);

  return (
    <div className="border border-grey-100 rounded-xl p-3 flex flex-wrap items-center gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-grey-500">{KIND_LABELS[candidate.attribute_kind] ?? candidate.attribute_kind}</span>
          <span className="font-mono text-sm font-medium text-grey-900">{candidate.value_norm}</span>
          {currentValue && (
            <span className="text-xs text-grey-400">vs current <span className="font-mono">{currentValue}</span></span>
          )}
        </div>
        <div className="text-xs text-grey-400 mt-0.5">
          source: {candidate.source} · confidence {Number(candidate.confidence).toFixed(2)} ·{' '}
          {candidate.observation_count} sighting{candidate.observation_count === 1 ? '' : 's'} · observed{' '}
          {format(new Date(candidate.observed_at), 'dd MMM yyyy')}
          {candidate.evidence_ref && (
            <span className="break-all"> · evidence: {candidate.evidence_ref.slice(0, 120)}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {confirming ? (
          <>
            <span className="text-xs text-grey-500 mr-1">
              {confirming === 'accept_correct' && 'Accept — the old value was wrong?'}
              {confirming === 'accept_rebrand' && 'Accept — the company changed (keeps history)?'}
              {confirming === 'reject' && 'Reject this candidate?'}
            </span>
            <button
              disabled={busy}
              onClick={() => { onAction(confirming); setConfirming(null); }}
              className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              Confirm
            </button>
            <button
              onClick={() => setConfirming(null)}
              className="px-2.5 py-1 text-xs font-medium rounded-lg text-grey-600 hover:bg-grey-50"
            >
              Back
            </button>
          </>
        ) : (
          <>
            <button
              disabled={busy}
              onClick={() => setConfirming('accept_correct')}
              title="We were wrong — replaces the current value as a correction"
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Correct
            </button>
            {currentValue && (
              <button
                disabled={busy}
                onClick={() => setConfirming('accept_rebrand')}
                title="The world changed — closes the old value as history (rebrand)"
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg text-sky-700 hover:bg-sky-50 disabled:opacity-50"
              >
                <CheckCircle2 className="h-3.5 w-3.5" /> Rebrand
              </button>
            )}
            <button
              disabled={busy}
              onClick={() => setConfirming('reject')}
              title="Not this company's — kept as a recorded refusal"
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg text-grey-500 hover:bg-grey-50 disabled:opacity-50"
            >
              <XCircle className="h-3.5 w-3.5" /> Reject
            </button>
          </>
        )}
      </div>
    </div>
  );
}
